import { getSummaryMetrics } from 'js/utils/question-set';
import standardCohortKeys from 'js/utils/cohort-keys';
import {
  isNoneCohortKey,
  matchBreakoutOnAssessment,
  resolveAssessmentCohorts,
  sortCohortLegendItems,
  unionAssessmentBreakouts,
} from 'js/utils/assessment-cohorts';
import {
  buildAssessmentRadarSeries,
  mergeQuestionSetsLatestWins,
  questionSetsHaveResponses,
} from '../assessment-radar-series';

export const COMBINED_LENS_NAME = 'Everyone';
export const NO_BREAKOUT_LENS_HEADER = 'No breakout';

export function combinedLensPickerItem() {
  return {
    id: standardCohortKeys.CUMULATIVE,
    kind: 'combined',
    lensKey: standardCohortKeys.CUMULATIVE,
    name: COMBINED_LENS_NAME,
    breakout: null,
    breakoutName: NO_BREAKOUT_LENS_HEADER,
  };
}

export function questionSetsForAssessmentLens({
  assessment,
  dataMap,
  lensKey,
  selectedBreakout,
}) {
  if (!assessment) {
    return null;
  }
  if (lensKey === standardCohortKeys.CUMULATIVE) {
    return dataMap?.assessments?.[assessment.id]?.questionSets ?? null;
  }
  const matched = matchBreakoutOnAssessment(assessment, selectedBreakout);
  if (!matched || matched.source === 'none') {
    return null;
  }
  const resolved = resolveAssessmentCohorts(assessment, matched.id);
  return getSummaryMetrics([assessment], {
    filter: { cohorts: { [lensKey]: true } },
    participantCohortsMap: resolved.participantCohortsMap,
  }).questionSets;
}

export function unionLensCohorts(assessments, selectedBreakout) {
  if (!selectedBreakout || selectedBreakout.source === 'none') {
    return [];
  }
  const byKey = new Map();
  (assessments ?? []).forEach((assessment) => {
    const matched = matchBreakoutOnAssessment(assessment, selectedBreakout);
    if (!matched || matched.source === 'none') {
      return;
    }
    resolveAssessmentCohorts(assessment, matched.id).cohorts.forEach(
      (cohort) => {
        const key = cohort.value ?? cohort.key;
        if (key != null && !byKey.has(key)) {
          byKey.set(key, cohort);
        }
      }
    );
  });
  return sortCohortLegendItems([...byKey.values()]);
}

export function lensPickerItemLabel(item) {
  if (!item || item.kind === 'combined') {
    return item?.name ?? COMBINED_LENS_NAME;
  }
  return [item.breakoutName, item.name].filter(Boolean).join(' › ');
}

export function cohortLensHasResponses({
  assessments,
  lensKey,
  selectedBreakout,
}) {
  return (assessments ?? []).some((assessment) => {
    const questionSets = questionSetsForAssessmentLens({
      assessment,
      dataMap: null,
      lensKey,
      selectedBreakout,
    });
    if (!questionSets) {
      return false;
    }
    return questionSetsHaveResponses(questionSets);
  });
}

export function buildLensPickerItems(assessments = []) {
  const combined = combinedLensPickerItem();
  const groups = [];

  unionAssessmentBreakouts(assessments)
    .filter((breakout) => breakout.source !== 'none')
    .forEach((breakout) => {
      const cohorts = unionLensCohorts(assessments, breakout).filter(
        (cohort) => {
          const key = cohort.value ?? cohort.key;
          return key != null && key !== standardCohortKeys.CUMULATIVE;
        }
      );
      const items = cohorts.flatMap((cohort) => {
        const lensKey = cohort.value ?? cohort.key;
        const disabled = !cohortLensHasResponses({
          assessments,
          lensKey,
          selectedBreakout: breakout,
        });
        if (disabled && isNoneCohortKey(lensKey)) {
          return [];
        }
        return [
          {
            id: `${breakout.unionKey}::${lensKey}`,
            kind: 'cohort',
            lensKey,
            name: cohort.text ?? cohort.name,
            breakout,
            breakoutName: breakout.name,
            disabled,
          },
        ];
      });
      if (items.length === 0) {
        return;
      }
      groups.push({
        breakout,
        breakoutName: breakout.name,
        items,
      });
    });

  const items = [combined, ...groups.flatMap((group) => group.items)];
  const enabledCohortCount = items.filter(
    (item) => item.kind === 'cohort' && !item.disabled
  ).length;

  return {
    combined,
    groups,
    items,
    showPicker: enabledCohortCount > 1,
  };
}

export function buildLensRadarSeries({
  assessments,
  dataMap,
  lensKey,
  selectedBreakout,
}) {
  const dated = assessments ?? [];
  const combinedQuestionSets =
    lensKey === standardCohortKeys.CUMULATIVE
      ? dataMap?.questionSets ?? {}
      : mergeQuestionSetsLatestWins(
          dated
            .map((assessment) =>
              questionSetsForAssessmentLens({
                assessment,
                dataMap,
                lensKey,
                selectedBreakout,
              })
            )
            .filter(
              (questionSets) =>
                questionSets && questionSetsHaveResponses(questionSets)
            )
        );

  const lensDataMap = {
    questionSets: combinedQuestionSets,
    assessments: {},
  };
  dated.forEach((assessment) => {
    const overallQuestionSets =
      dataMap?.assessments?.[assessment.id]?.questionSets;
    if (!questionSetsHaveResponses(overallQuestionSets)) {
      return;
    }
    const questionSets = questionSetsForAssessmentLens({
      assessment,
      dataMap,
      lensKey,
      selectedBreakout,
    });
    lensDataMap.assessments[assessment.id] = {
      questionSets: questionSets ?? {},
    };
  });

  const series = buildAssessmentRadarSeries(lensDataMap, dated, {
    includeCombined: true,
    combinedQuestionSets,
    includeEmptyAsDisabled: true,
  });

  return {
    combinedQuestionSets,
    ...series,
  };
}
